<template>
  <div ref="containerRef" class="container container--no-padding" :class="$style['category']">
    <StickOnScroll :test-element="navigationRef?.$el" :trigger-hide-element="endOfContentRef">
      <LazyCategoryNavigation
        :class="$style['category__navigation']"
        :category="category"
        :parent="parent" />
    </StickOnScroll>
    <LazySkipLinks
      :items="[
        {
          id: 'products',
          title: 'Go to products'
        },
        {
          id: 'filters',
          title: 'Go to filters'
        },
        {
          id: 'footer',
          title: 'Go to footer'
        }
      ]" />
    <aside
      id="filters"
      :class="$style['category__filters']">
      <LazyFilters />
    </aside>

    <div
      :class="$style['category__main']">
      <LazyCategoryNavigation
        ref="navigationRef"
        :class="$style['category__navigation']"
        :category="category"
        :parent="parent" />

      <nav
        v-if="subNavigation?.length"
        :class="$style['category__subnavigation']">
        <LazyCircleList
          :disabled="loading"
          data-test-id="category-subnavigation"
          :active="`/${$lang}/${category.slugs[$lang]}`"
          :items="subNavigation" />
      </nav>

      <LazyCategorySizeSelector />
      <section
        id="products"
        ref="containerRef"
        :class="[$style['category__products'],
                 $style[`category__products--${cardSize}`]]">
        <LazyCategoryUsps v-if="uspRow" :usp-row="uspRow" />
        <ProductCard
          v-for="(product, index) in products"
          :key="'cId' + product.cId"
          ref="productRef"
          :card-size="cardSize"
          :product="product"
          :loading="index < 4 ? 'auto' : 'lazy'"
          data-test-id="product-card"
          :to="getProductRoute({ product, lang: $lang})"
          @click="dataLayerProductClick(product)">
          <template
            #card-button>
            <LazyButtonLike
              :product="product"
              size="small"
              no-animate
              data-test-id="wishlist-button"
              :aria-label="$t('general/add-to-wishlist')" />
          </template>
        </ProductCard>
        <div
          v-for="promotion in promotions"
          :key="promotion.id"
          :class="[$style['category__promotion'],
                   {[$style['category__promotion--large']] : cardSize === 'large'}
          ]"
          :style="{ 'grid-row': promotion.row }">
          <IntersectionObserver root-margin="10px">
            <LazyPromotions :promotion-data="promotion" />
          </IntersectionObserver>
        </div>
        <div
          v-if="!loading && total === 0"
          :class="$style['category__products__message']">
          <h4>{{ $t('general/no-products-found') }}</h4>
        </div>
      </section>

      <LazyIntersectionObserver
        root-margin="1000px"
        @intersect="loadMore" />

      <div v-if="!finished && categoryData && products.size" :class="$style['category__loadmore']">
        <LazyProgress :value="total" :max="filterStore.totalProducts" :title="`${total} ${$t('general/of')} ${filterStore.totalProducts}`" />
        <LazyBtn color="cta" :loading="loading" shape="pill" class="h-margin-top" @click="loadMore">
          {{ $t('general/load-more') }}
        </LazyBtn>
      </div>

      <section
        v-if="categoryData"
        ref="endOfContentRef"
        :class="[
          $style['category__summary'],
          {
            [$style['category__summary--show']]:
              finished
          }
        ]">
        <div
          :class="$style['category__summary__inner']"
          class="container--small">
          <LazySummaryText :summary="categoryData.summary" />
        </div>
      </section>
    </div>

    <LazyModal
      v-if="rootStore.filtersOpen"
      show-border
      @close="rootStore.filtersOpen = false">
      <LazyFilters />

      <template #modal-footer>
        <LazyBtn

          full-width="all"
          color="cta"
          shape="pill"
          @click="rootStore.filtersOpen = false">
          {{ filterStore.totalProducts }}
          {{ $t('general/products').toLowerCase() }}
        </LazyBtn>
      </template>
    </LazyModal>
  </div>
</template>
<script setup>
import { debounce } from '~/helpers/debounce'
import joinQueryParams from '~/helpers/join-query-params'
import getProductRoute from '~/helpers/get-product-route'
const config = useRuntimeConfig()
const { $lang, $bus } = useNuxtApp()
const rootStore = useRootStore()
const filterStore = useFilterStore()
const route = useRoute()
const router = useRouter()
const { loadPromotions, promotionsForSlug } = usePromotions()
const { products, lastProductBatch, category, total, parent, finished, loading, load: loadProducts, nextPage, setCategory, clear: clearProducts, subNavigation } = useProducts()
const { getCategoryBySlug, getSubCategoryBySlug } = useNavigation()
const productRef = ref(null)
const containerRef = ref(null)
const endOfContentRef = ref(null)
const navigationRef = ref(null)
const observer = ref(null)
let productsPerRow = 2 // default for promo placement
await useI18n(['usps', 'account'])
const navigationCategory = getCategoryBySlug(route.params.slug[0]?.toLowerCase())
const navigationSubCategory = getSubCategoryBySlug(route.params.slug[0]?.toLowerCase(), route.params.slug[1]?.toLowerCase())
rootStore.showFooter = false
rootStore.footerInsetLeft = true
const cardSize = useCookie('cardSize')

const props = defineProps({
  categoryData: {
    type: Object,
    default: null
  }
})

const promotions = computed(() => {
  if (total.value < 4) {
    return false
  }
  return promotionsForSlug({
    lang: $lang,
    slug: route.path.substr(1),
    total: total.value,
    productsPerRow
  })
})

const uspRow = computed(() => {
  if (total.value < 4 || !promotions.value) {
    return false
  }
  let uspRow = 3
  promotions.value
    .forEach(promotion => {
      if (!promotion?.row) { return }
      if (uspRow === promotion.row) {
        uspRow++
      }
    })

  return uspRow
})

const scrollToTop = () => {
  window.scrollTo(0, 0)
}

const dataLayerProductClick = product => {
  $bus.$emit('product-click', {
    sourceComponent: 'Category page',
    product,
    category: props.categoryData
  })
}

const linkPreloadImages = () => {
  const returnData = []
  Array.from(products.value).slice(0, 8).forEach(product => {
    returnData.push({
      rel: 'preload',
      href: `${config.public.cdnURL}pictures/medium/${product.images[0]}.webp`,
      as: 'image'
    })
  })

  return returnData
}

const addImpressionsDatalayer = () => {
  $bus.$emit('add-impressions', {
    eventId: Date.now(),
    products: lastProductBatch.value,
    sourceUrl: route.path,
    category: category.value
  })
}

const loadMore = debounce(async () => {
  await nextPage(route.query)

  if (!finished.value) {
    addImpressionsDatalayer()
  }
  if (finished.value) {
    rootStore.showFooter = true
  }
}, 300)

await filterStore.reset()

await setCategory({
  categorySlug: route.params.slug[0]?.toLowerCase(),
  subCategorySlug: route.params.slug[1]?.toLowerCase() || null
})

if (products.value) {
  rootStore.showFooter = products.value.finished
}

await filterStore.setFromQuery(route.query)
await clearProducts()
await filterStore.load({
  params: {
    ...category?.value?.params
  },
  navigationFilters: !subNavigation.value.length
    ? navigationCategory.filters
    : navigationSubCategory.filters
})

if (route.query && Object.keys(route.query).length) {
  filterStore.update({
    params: {
      ...joinQueryParams(route.query, category.value.params)
    },
    navigationFilters: !subNavigation.value.length
      ? navigationCategory.filters
      : navigationSubCategory.filters
  })
}

loadPromotions()
await loadProducts(route.query)

watch(() => route.query, async (to, from) => {
  if (JSON.stringify(to) === JSON.stringify(from)) { return }
  await filterStore.setFromQuery(route.query)
  await loadPromotions()
  await clearProducts()
  await loadProducts(route.query)

  await filterStore.update({
    params: {
      ...joinQueryParams(route.query, category.value.params)
    },
    navigationFilters: !subNavigation.value.length
      ? navigationCategory.filters
      : navigationSubCategory.filters
  })

  rootStore.showFooter = products.value.finished
})

watch(() => filterStore.forceUpdate, debounce(async () => {
  scrollToTop()
  await router
    .push({
      query: filterStore.queryString
    })
    .catch(() => { })
}, 500, true))

onActivated(async () => {
  if (productRef.value) {
    productsPerRow = Math.floor(containerRef.value?.getBoundingClientRect().width / productRef.value[0]?.$el.getBoundingClientRect().width)
  }
  await filterStore.reset()
  await filterStore.load({
    params: {

      ...joinQueryParams(route.query, category.value.params)
    },
    navigationFilters: !subNavigation.value.length
      ? navigationCategory.filters
      : navigationSubCategory.filters
  })
  await addImpressionsDatalayer()
  rootStore.showFooter = false
  rootStore.footerInsetLeft = true
})

onDeactivated(() => {
  rootStore.showFooter = true
  rootStore.footerInsetLeft = false
})

onUnmounted(() => {
  document.removeEventListener('scroll', () => {})
  rootStore.showFooter = true
  rootStore.footerInsetLeft = false
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
})

onMounted(() => {
  if (productRef.value) {
    productsPerRow = Math.floor(containerRef.value?.getBoundingClientRect().width / productRef.value[0]?.$el.getBoundingClientRect().width)
  }
  rootStore.showFooter = false
  addImpressionsDatalayer()
})

await useHead({
  link: [
    ...linkPreloadImages()
  ]
})

</script>
<style lang="scss" module>
.category {
  display: flex;

  &__subnavigation {
    z-index: z-index(categorynavigation) - 1;
    padding: $spacing * 2;
  }

  &__summary {
    padding: $spacing * 8 $spacing * 2;
    opacity: 0;
    transition: opacity $transition-time-slowest $transition-ease-in;

    &--show {
      opacity: 1;
    }

    &__inner {
      margin: 0 auto;
    }
  }

  &__scrolltop {
    position: fixed;
    bottom: m#{a}x(env(safe-area-inset-bottom), #{$spacing * 2});
    right: $spacing * 2;
    z-index: 3;

    @media (min-width: #{$breakpoint-tablet}) {
      display: none;
    }
  }

  &__filters {
    @include scrollbar;

    /* stylelint-disable no-duplicate-selectors */
    & {
      background-color: $broken-white;
      color: transparent;
      overflow-y: auto;
      border-right: 1px solid $grey-lighter;
      padding-block: $spacing * 3;
      position: fixed;
      top: var(--toolbar-height);
      bottom: 0;
      height: auto;
      width: $filters-width;
      z-index: 1;
      transition: color $transition-time-slow $transition-easing;
    }

    &:hover {
      color: $grey;
    }
  }

  &__main {
    width: calc(100vw - #{$filters-width});

    @media (min-width: #{$breakpoint-tablet}) {
      padding-left: $filters-width;
      width: 100%;
    }
  }

  &__promotion {
    grid-column: 1 / 3;
    grid-row: auto;
    overflow: hidden;
    height: 100%;

    &--large {
      @media (max-width: #{$breakpoint-mobile}) {
        grid-column: 1 / 2;
      }
    }
  }

    &__loadmore {
    align-items: center;
    color: $grey-darker;
    display: flex;
    gap: $spacing;
    flex-direction: column;
    justify-content: center;
    grid-column: 1 / -1;
    width: 100%;
}

  &__products {
    padding: $spacing * 2;

    @include productsGrid;

    &--small {
      @include smallProductsGrid;
    }

    &--medium {
      @include productsGrid;
    }

    &--large {
      @include largeProductsGrid;
    }

    &__message {
      align-items: center;
      justify-content: center;
      display: flex;
      grid-column: 1/ -1; // All columns
      grid-row: 1 / -1;
      width: 100%;
      text-align: center;
    }
  }

  @media (max-width: #{$breakpoint-tablet - 1}) {
    &__filters {
      display: none;
    }

    &__main {
      width: 100%;
    }
  }
}
</style>
